import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Phone,
  Mail,
  MapPin,
  Shield,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileText,
  Upload,
  Calendar,
  ChevronLeft,
  Save
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * My Profile — Caregiver Self-Service
 * 
 * Caregivers can update their own:
 * - Contact info (email, phone, address)
 * - Emergency contact
 * - Documents (certifications, etc.)
 * 
 * They CANNOT change:
 * - Name, DOB (requires staff approval)
 * - Employment details
 * - Client assignments
 */

const DOCUMENT_TYPES = [
  { key: 'drivers_license', label: "Driver's License", canUpload: true },
  { key: 'cna_certification', label: 'CNA Certification', canUpload: true },
  { key: 'hha_certification', label: 'HHA Certification', canUpload: true },
  { key: 'cpr_certification', label: 'CPR Certification', canUpload: true },
  { key: 'tb_test', label: 'TB Test Results', canUpload: true },
  { key: 'car_insurance', label: 'Car Insurance', canUpload: true },
  { key: 'car_registration', label: 'Car Registration', canUpload: true },
];

export default function MyProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMyProfile();
  }, []);

  const fetchMyProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/data-entry/my-profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setFormData({
          phone: data.phone || '',
          phone_alt: data.phone_alt || '',
          email: data.email || '',
          address_line1: data.address_line1 || '',
          address_line2: data.address_line2 || '',
          city: data.city || '',
          state: data.state || '',
          zip_code: data.zip_code || '',
        });
      } else if (res.status === 404) {
        // No profile yet - show empty form
        setProfile({ status: 'new' });
      } else {
        setError('Could not load your profile. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Could not connect to server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/data-entry/my-profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setProfile(updated);
        setHasChanges(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        const errorData = await res.json();
        setError(errorData.detail || 'Could not save changes. Please try again.');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Could not connect to server. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('document_type', docType);
      
      const res = await fetch(`${API_URL}/api/data-entry/my-profile/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formDataUpload
      });
      
      if (res.ok) {
        // Refresh profile to show new document
        await fetchMyProfile();
      } else {
        setError('Could not upload document. Please try again.');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Could not upload document. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-[#176DFF] animate-spin" />
          <p className="text-sm text-[#64748B]">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:px-6 sm:py-8">
        {/* Header */}
        <header className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#176DFF]/10 flex items-center justify-center">
              <User className="w-7 h-7 sm:w-8 sm:h-8 text-[#176DFF]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-[#0D1B33]">
                {profile?.first_name ? `${profile.first_name} ${profile.last_name}` : user?.name || 'My Profile'}
              </h1>
              <p className="text-sm text-[#64748B]">Update your contact information</p>
            </div>
          </div>
        </header>

        {/* Status Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        
        {saveSuccess && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-sm text-emerald-700">Your changes have been saved.</p>
          </div>
        )}

        {/* Contact Information */}
        <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
          <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-[#E2E8F0]">
            <h2 className="font-semibold text-[#0D1B33]">Contact Information</h2>
          </div>
          <div className="p-4 sm:p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Phone"
                field="phone"
                type="tel"
                value={formData.phone}
                onChange={handleFieldChange}
                icon={Phone}
                placeholder="(555) 123-4567"
              />
              <FormField
                label="Alternate Phone"
                field="phone_alt"
                type="tel"
                value={formData.phone_alt}
                onChange={handleFieldChange}
                icon={Phone}
                placeholder="(555) 123-4567"
              />
            </div>
            <FormField
              label="Email"
              field="email"
              type="email"
              value={formData.email}
              onChange={handleFieldChange}
              icon={Mail}
              placeholder="you@example.com"
            />
          </div>
        </section>

        {/* Address */}
        <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
          <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-[#E2E8F0]">
            <h2 className="font-semibold text-[#0D1B33]">Address</h2>
          </div>
          <div className="p-4 sm:p-5 space-y-4">
            <FormField
              label="Street Address"
              field="address_line1"
              value={formData.address_line1}
              onChange={handleFieldChange}
              icon={MapPin}
              placeholder="123 Main St"
            />
            <FormField
              label="Apt, Suite, Unit (optional)"
              field="address_line2"
              value={formData.address_line2}
              onChange={handleFieldChange}
              placeholder="Apt 4B"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <FormField
                label="City"
                field="city"
                value={formData.city}
                onChange={handleFieldChange}
                placeholder="Los Angeles"
                className="col-span-2 sm:col-span-1"
              />
              <FormField
                label="State"
                field="state"
                value={formData.state}
                onChange={handleFieldChange}
                placeholder="CA"
              />
              <FormField
                label="ZIP Code"
                field="zip_code"
                value={formData.zip_code}
                onChange={handleFieldChange}
                placeholder="90001"
              />
            </div>
          </div>
        </section>

        {/* Documents */}
        <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
          <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-[#E2E8F0]">
            <h2 className="font-semibold text-[#0D1B33]">My Documents</h2>
            <p className="text-xs text-[#64748B] mt-1">Upload updated certifications and documents</p>
          </div>
          <div className="p-4 sm:p-5">
            <div className="space-y-3">
              {DOCUMENT_TYPES.map((docType) => {
                const uploaded = profile?.documents?.find(d => d.document_type === docType.key);
                return (
                  <div
                    key={docType.key}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      uploaded ? 'border-emerald-200 bg-emerald-50' : 'border-[#E2E8F0]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {uploaded ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                      ) : (
                        <FileText className="w-5 h-5 text-[#94A3B8] shrink-0" />
                      )}
                      <div>
                        <span className="text-sm font-medium text-[#0D1B33]">{docType.label}</span>
                        {uploaded && (
                          <p className="text-xs text-[#64748B]">{uploaded.file_name}</p>
                        )}
                      </div>
                    </div>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentUpload(e, docType.key)}
                      />
                      <span className="text-sm text-[#176DFF] hover:underline flex items-center gap-1">
                        <Upload className="w-4 h-4" />
                        {uploaded ? 'Update' : 'Upload'}
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* HIPAA Notice */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-900">Your information is protected</p>
            <p className="text-xs text-blue-700">All data is encrypted and stored securely in compliance with HIPAA regulations.</p>
          </div>
        </div>

        {/* Save Button - Fixed on mobile */}
        <div className="sticky bottom-4 sm:relative sm:bottom-auto">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`w-full py-3 text-base ${
              hasChanges 
                ? 'bg-[#176DFF] hover:bg-[#176DFF]/90' 
                : 'bg-[#E2E8F0] text-[#94A3B8] cursor-not-allowed'
            }`}
            data-testid="save-profile-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Form Field Component - Mobile optimized
function FormField({ label, field, value, onChange, type = 'text', icon: Icon, placeholder, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-[#334155] mb-1.5">
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        )}
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(field, e.target.value)}
          placeholder={placeholder}
          className={`w-full px-3 py-3 text-base border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF] ${
            Icon ? 'pl-10' : ''
          }`}
        />
      </div>
    </div>
  );
}
